
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Twitter, Check } from 'lucide-react';
import { linkTwitterHandle } from '@/lib/engagement/twitter';
import { toast } from "sonner";

interface InitiationTwitterProps {
  onComplete: (data: { twitterHandle: string }) => void;
}

const InitiationTwitter = ({ onComplete }: InitiationTwitterProps) => {
  const [twitterHandle, setTwitterHandle] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  const handleLinkTwitter = async () => {
    if (!twitterHandle.trim()) {
      toast.error('Please enter your Twitter handle');
      return;
    }

    setIsLinking(true);
    
    try {
      const cleanHandle = twitterHandle.replace('@', '').trim();
      const result = await linkTwitterHandle(cleanHandle);
      
      if (result.success) {
        toast.success('Twitter account linked successfully!');
        onComplete({
          twitterHandle: cleanHandle
        });
      } else {
        toast.error(result.error || 'Failed to link Twitter account');
      }
      
    } catch (error) {
      console.error('Twitter linking error:', error);
      toast.error('Failed to link Twitter. Please try again.');
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <Twitter className="mx-auto mb-4 text-blue-400" size={48} />
        <h3 className="text-xl font-bold text-tan mb-2">Link Twitter Account</h3>
        <p className="text-tan-dark">
          Connect your Twitter to unlock social features and earn bonus points
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-tan mb-2">
            Twitter Handle
          </label>
          <Input
            type="text"
            placeholder="@yourusername"
            value={twitterHandle}
            onChange={(e) => setTwitterHandle(e.target.value)}
            className="w-full bg-navy-dark border-tan-dark text-tan"
          />
        </div>
        
        <div className="bg-navy-dark p-4 rounded-lg">
          <h4 className="text-tan font-medium mb-2">Benefits of linking:</h4>
          <ul className="space-y-1 text-sm text-tan-dark">
            <li className="flex items-center gap-2">
              <Check size={14} className="text-green-400" />
              Participate in tweet missions
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} className="text-green-400" />
              Earn engagement rewards
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} className="text-green-400" />
              Access to exclusive content
            </li>
          </ul>
        </div>
        
        <Button 
          onClick={handleLinkTwitter}
          disabled={isLinking || !twitterHandle.trim()}
          className="w-full bg-tan text-navy hover:bg-tan-dark"
        >
          {isLinking ? 'Linking...' : 'Link Twitter Account'}
        </Button>
      </div>
    </div>
  );
};

export default InitiationTwitter;
